// RITA-1549 — Die Druckeingabe für printContractCreation aus dem Fenster „Vertrag erstellen“ (Spalten mit Vorlage
// und eingesetzten Werten, Herkunft `template`) bzw. von der Bearbeiten-Seite (Editorstand, Herkunft `draft`),
// jeweils nur die angehakten Dokumente in der Reihenfolge Anschreiben, Vertrag (Skizze 3c, F4/F5/F11).
//
// Das Fenster druckt genau den Stand der Vorschau: Platzhalter werden zu Text wie beim Entwurf
// (`platzhalterAlsText`), nur HEUTIGES_DATUM bleibt Platzhalter und bekommt vom Server den Drucktag. Die
// Vorprüfung entspricht der vor dem Speichern (useEntwurfSpeichern): leer, fremder Platzhalter, 512 kB je Dokument,
// 900 kB gesamt – sonst käme statt eines Nutzertexts ein GraphQL-Fehler. Zu groß meldet sie mit eigenem Wortlaut
// (DRUCK_ZU_GROSS), weil hier nichts gespeichert wird.
//
// RITA-1552: Die Eingabe entsteht je Vorgang (`arten`, Vorgabe Vertrag = Anschreiben Versand und Vertrag; Rücksendung
// = nur das Anschreiben Rücksendung, services/vorgang.ts). Die Rückfrage „Es fehlen Angaben“ im Fenster (Skizze
// Rücklauf, Bild F a) nennt die Lücken nur der angehakten, druckbaren Dokumente (fehlendeAngabenDerAuswahl); sie steht
// hier und nicht in fehlendeAngaben.ts, weil diese Datei von dort importiert (kein Import-Zyklus).
import type {
  ContractCreationKind,
  ContractCreationPrintDocumentInput,
  ContractCreationPrintInput,
} from 'global-intern-types';
import {
  DOKUMENT_MAX_BYTES,
  hatSichtbarenText,
  speicherText,
  utf8Bytes,
} from '../../templates/contractEditor/model/speicherform';
import type { Vertragsdokument } from '../../templates/contractEditor/model/types';
import { BEWEGLICHE_PLATZHALTER, platzhalterAlsText } from './entwurfsdokument';
import { FehlendeAngabe, fehlendeAngaben, verwendetePlatzhalter } from './fehlendeAngaben';
import type { Bearbeitername } from './vertragPlatzhalterwerte';
import { DRUCK_KEINE_AUSWAHL, DRUCK_ZU_GROSS } from './vertragsdruckTexte';
import { ART_STELLE } from './vertragserstellungTexte';
import { leerText, PLATZHALTER_FEHLER } from './vertragsentwurfTexte';
import { ALLE_ARTEN, VORGANG_ARTEN } from './vorgang';

/** Gleiche Grenze wie beim Entwurf (ENTWURF_MAX_BYTES) und im Server: beide Dokumente zusammen. */
export const DRUCK_MAX_BYTES = 900 * 1024;

/**
 * Feste Reihenfolge der Dokumente in der PDF: Anschreiben Versand vor Vertrag; RITA-1552: das Anschreiben Rücksendung
 * danach (es wird immer allein gedruckt).
 */
export const DRUCK_ARTEN: readonly ContractCreationKind[] = ALLE_ARTEN;

/** Die Arten eines Vorgangs in der Reihenfolge der PDF. */
const inDruckreihenfolge = (arten: readonly ContractCreationKind[]) => DRUCK_ARTEN.filter((art) => arten.includes(art));

/** Häkchen je Art; fehlt eine Art, gilt sie als nicht angehakt. */
export type Druckauswahl = Partial<Record<ContractCreationKind, boolean>>;

/** Eine Spalte des Fensters: gewählte Vorlage (mit Stand) und das gelesene Dokument mit Platzhaltern. */
export interface Druckspalte {
  kind: ContractCreationKind;
  template: { id: number; name: string; updated_at?: string | null } | null;
  dokument: Vertragsdokument | null;
  /** RITA-1552: Platzhalter, die nicht mehr im Katalog stehen (für die Rückfrage „Es fehlen Angaben“). */
  unbekanntePlatzhalter?: string[];
}

/** Ein Teil der Bearbeiten-Seite: der Speicherstand des Editors und die Herkunft aus dem Entwurf. */
export interface Druckteil {
  kind: ContractCreationKind;
  dokument: Vertragsdokument;
  herkunft: { template_id: number; template_name: string; template_updated_at: string };
}

export type Platzhalterwerte = Readonly<Partial<Record<string, string>>>;

const alsJson = (dokument: Vertragsdokument) => dokument as unknown as Record<string, unknown>;
const alsDokument = (json: Record<string, unknown>) => json as unknown as Vertragsdokument;

/** Nur Spalten mit lesbarem Dokument und Vorlagenstand lassen sich drucken (Häkchen sonst gesperrt). */
export function druckbar(spalte: Druckspalte): boolean {
  return !!spalte.dokument && !!spalte.template?.updated_at;
}

/** Fenster: angehakte, druckbare Spalten mit eingesetzten Werten, Herkunft `template`; nur die `arten` des Vorgangs. */
export function druckeingabeAusSpalten(
  spalten: Druckspalte[],
  werte: Platzhalterwerte,
  auswahl: Druckauswahl,
  arten: readonly ContractCreationKind[] = VORGANG_ARTEN.contract,
): ContractCreationPrintInput {
  const documents = inDruckreihenfolge(arten).flatMap((art): ContractCreationPrintDocumentInput[] => {
    const spalte = spalten.find((eintrag) => eintrag.kind === art);
    if (!spalte || !auswahl[art] || !spalte.dokument || !spalte.template || !spalte.template.updated_at) return [];
    return [
      {
        kind: art,
        template_id: spalte.template.id,
        template_name: spalte.template.name,
        template_updated_at: spalte.template.updated_at,
        document: alsJson(platzhalterAlsText(spalte.dokument, werte)),
        origin: 'template',
      },
    ];
  });
  return { documents };
}

/**
 * Bearbeiten-Seite: angehakte Teile mit dem sichtbaren Editorstand, Herkunft `draft` (F5: kein Stopp); nur die `arten`
 * des Vorgangs.
 */
export function druckeingabeAusEditoren(
  teile: Druckteil[],
  auswahl: Druckauswahl,
  arten: readonly ContractCreationKind[] = VORGANG_ARTEN.contract,
): ContractCreationPrintInput {
  const documents = inDruckreihenfolge(arten).flatMap((art): ContractCreationPrintDocumentInput[] => {
    const teil = teile.find((eintrag) => eintrag.kind === art);
    if (!teil || !auswahl[art]) return [];
    return [
      {
        kind: art,
        template_id: teil.herkunft.template_id,
        template_name: teil.herkunft.template_name,
        template_updated_at: teil.herkunft.template_updated_at,
        document: alsJson(teil.dokument),
        origin: 'draft',
      },
    ];
  });
  return { documents };
}

/** Nutzertext, wenn die Eingabe nicht gedruckt werden kann; null, wenn sie zum Server darf. */
export function vorpruefung(eingabe: ContractCreationPrintInput): string | null {
  if (eingabe.documents.length === 0) return DRUCK_KEINE_AUSWAHL;
  const dokumente = eingabe.documents.map((eintrag) => ({
    kind: eintrag.kind,
    dokument: alsDokument(eintrag.document),
  }));
  const leer = dokumente.find(({ dokument }) => !hatSichtbarenText(dokument));
  if (leer) return leerText(leer.kind);
  // Eingefügter Text kann Platzhalter enthalten (etwa aus einer Vorlage kopiert); der Server lehnt sie ab.
  const fremderPlatzhalter = dokumente.some(({ dokument }) =>
    verwendetePlatzhalter(dokument).some((schluessel) => !BEWEGLICHE_PLATZHALTER.includes(schluessel)),
  );
  if (fremderPlatzhalter) return PLATZHALTER_FEHLER;
  if (dokumente.some(({ dokument }) => utf8Bytes(speicherText(dokument)) > DOKUMENT_MAX_BYTES)) return DRUCK_ZU_GROSS;
  if (utf8Bytes(JSON.stringify(eingabe)) > DRUCK_MAX_BYTES) return DRUCK_ZU_GROSS;
  return null;
}

/** F11: Nur wenn alle im Entwurf enthaltenen Dokumente angehakt sind, darf der Druck den Entwurf erledigen. */
export function alleEnthaltenAngehakt(vorhanden: ContractCreationKind[], auswahl: Druckauswahl): boolean {
  return vorhanden.length > 0 && vorhanden.every((art) => !!auswahl[art]);
}

/**
 * RITA-1552 (Skizze Rücklauf, Bild F a; F9): Fehlende Angaben für die Rückfrage „Es fehlen Angaben“ im Fenster – dieselbe
 * Liste wie im gelben Kasten (fehlendeAngaben), aber nur für die angehakten, druckbaren Dokumente; unbekannte
 * Platzhalter und ein fehlender Name der Bearbeiterin stehen mit drin. Leer = sofort drucken.
 */
export function fehlendeAngabenDerAuswahl(
  spalten: Druckspalte[],
  auswahl: Druckauswahl,
  werte: Platzhalterwerte,
  bearbeiter: Bearbeitername,
): FehlendeAngabe[] {
  return fehlendeAngaben(
    inDruckreihenfolge(spalten.map((spalte) => spalte.kind)).flatMap((art) => {
      const spalte = spalten.find((eintrag) => eintrag.kind === art);
      if (!spalte || !auswahl[art] || !druckbar(spalte) || !spalte.dokument) return [];
      return [
        {
          stelle: ART_STELLE[art],
          dokument: spalte.dokument,
          unbekanntePlatzhalter: spalte.unbekanntePlatzhalter ?? [],
        },
      ];
    }),
    werte,
    bearbeiter,
  );
}

/** RITA-1599: Das Dokument, das beim Druck aus dem Fenster fehlen würde, weil keine Vorlage dafür passt. */
export type FehlendesDokument = 'contract' | 'dispatch_letter';

/**
 * RITA-1599 (Fund 34): Lässt sich der Vertrag nicht drucken – keine oder mehrere gleich gute Vorlagen, oder die gefundene
 * ist nicht lesbar bzw. ohne Änderungsstand (Abschlussreview B2) –, würde nur das Anschreiben gedruckt, das aber einen
 * Vertrag ankündigt; umgekehrt nur der Vertrag ohne Anschreiben. Dann fragt das Fenster nach. Entscheidend ist die
 * fehlende druckbare Vorlage, nicht das Häkchen: Wer einen druckbaren Vertrag bewusst abwählt, sieht schon den Hinweis an
 * der Spalte. Nur der Vorgang Vertrag hat beide Dokumente; sonst null.
 */
export function fehlendesDokument(spalten: Druckspalte[], auswahl: Druckauswahl): FehlendesDokument | null {
  const vertrag = spalten.find((spalte) => spalte.kind === 'contract');
  const anschreiben = spalten.find((spalte) => spalte.kind === 'dispatch_letter');
  if (!vertrag || !anschreiben) return null;
  const gedruckt = (spalte: Druckspalte) => druckbar(spalte) && !!auswahl[spalte.kind];
  if (!druckbar(vertrag) && gedruckt(anschreiben)) return 'contract';
  if (!druckbar(anschreiben) && gedruckt(vertrag)) return 'dispatch_letter';
  return null;
}
